import { ReactNode, useMemo } from 'react';
import * as S from './styles';
import { animationTimingForAllStages, animationTimingWithoutStartStage } from './styles';

export type AnimationStageValues = {
  startValue: number;
  endValue: number;
  incrementValue: number;
};

export type AnimatedNumberIncrementProps = {
  startValue: number;
  endValue: number;
  disabled?: boolean;
  renderDisabledStage?: (props: AnimationStageValues) => ReactNode;
  renderStartStage?: (props: AnimationStageValues) => ReactNode;
  renderIncrementStage: (props: AnimationStageValues) => ReactNode;
  renderEndStage: (props: AnimationStageValues) => ReactNode;
};

export const AnimatedNumberIncrement = ({
  startValue,
  endValue,
  disabled,
  renderDisabledStage,
  renderStartStage,
  renderIncrementStage,
  renderEndStage,
}: AnimatedNumberIncrementProps) => {
  const values = useMemo(
    () => ({
      startValue,
      endValue,
      incrementValue: endValue - startValue,
    }),
    [startValue, endValue],
  );

  const animationTiming = renderStartStage
    ? animationTimingForAllStages
    : animationTimingWithoutStartStage;
  const internalRenderDisabledStage = renderDisabledStage
    ? renderDisabledStage
    : renderStartStage
      ? renderStartStage
      : renderIncrementStage;

  if (disabled || startValue === endValue) {
    return (
      <S.Container data-testid="animated-number-disabled">
        <S.DisabledStage>{internalRenderDisabledStage(values)}</S.DisabledStage>
      </S.Container>
    );
  }

  return (
    <S.Container>
      {renderStartStage && (
        <S.StartStage data-testid="animated-number-start-stage" {...animationTiming}>
          {renderStartStage(values)}
        </S.StartStage>
      )}
      <S.IncrementStage data-testid="animated-number-increment-stage" {...animationTiming}>
        {renderIncrementStage(values)}
      </S.IncrementStage>
      <S.EndStage data-testid="animated-number-end-stage" {...animationTiming}>
        {renderEndStage(values)}
      </S.EndStage>
    </S.Container>
  );
};
